import { SubsidyAllowanceKind } from '@/gql/graphql';
import type {
  PartnerMemberAllowanceBalance,
  PartnerMemberAllowanceBalances,
  SubsidyAllowanceBalanceCreateInput,
  SubsidyAllowanceBalanceUpdateInput,
} from '@/models/PartnerMemberAllowanceBalances';
import type { PartnerSubsidyAllowance } from '@/models/PartnerSubsidyAllowance';

export const BUDGET_ALLOWANCE_KINDS = new Set<string>([SubsidyAllowanceKind.BudgetRides, SubsidyAllowanceKind.BudgetCredits]);
export const RIDES_ALLOWANCE_KINDS = new Set<string>([SubsidyAllowanceKind.Rides, SubsidyAllowanceKind.BudgetRides]);
export const CREDITS_ALLOWANCE_KINDS = new Set<string>([SubsidyAllowanceKind.Credits, SubsidyAllowanceKind.BudgetCredits]);

export type BalanceFormRow = {
  key: string;
  id: number | null;
  amount: number;
  overrideRenewAmount: number | null;
  startBudgetAmount: number;
  beginningDate: string;
  suspensionDate: string;
  preAuthorizationNumber: string;
};

export type AllowanceFormGroup = {
  allowanceId: number;
  allowanceName: string;
  kind: string;
  isBudget: boolean;
  budgetLimit: number | null;
  isPreAuthorizationNumberRequired: boolean;
  balances: BalanceFormRow[];
};

export const isBudgetAllowanceKind = (kind: string) => BUDGET_ALLOWANCE_KINDS.has(kind);
export const isRidesAllowanceKind = (kind: string) => RIDES_ALLOWANCE_KINDS.has(kind);
export const getAmountPrecision = (kind: string) => (RIDES_ALLOWANCE_KINDS.has(kind) ? 0 : 2);
export const formatWithUnit = (value: string, kind: string) => (RIDES_ALLOWANCE_KINDS.has(kind) ? value : `$${value}`);
export const getBudgetKindUnitLabel = (kind: string) => (RIDES_ALLOWANCE_KINDS.has(kind) ? 'rides' : 'credits');

export type BudgetHeadlineLine = { label: string; value: string };

export const getBudgetHeadlineLines = (
  balance: BalanceFormRow,
  kind: string,
  amountLabel: string,
  preAuthorizationNumberLabel?: string,
  beginningDateLabel?: string,
  suspensionDateLabel?: string,
): BudgetHeadlineLine[] => {
  const formatAmount = (value: number) => (getAmountPrecision(kind) === 0 ? String(value) : value.toFixed(2));
  const formattedAmount = formatWithUnit(formatAmount(balance.amount), kind);
  const formattedStartAmount = formatWithUnit(formatAmount(balance.startBudgetAmount), kind);
  const beginningDate = balance.beginningDate || '—';
  const suspensionDate = balance.suspensionDate || '—';
  const lines: BudgetHeadlineLine[] = [
    { label: amountLabel, value: `${formattedAmount} / ${formattedStartAmount} ${getBudgetKindUnitLabel(kind)}` },
  ];
  if (beginningDateLabel || suspensionDateLabel) {
    lines.push({ label: beginningDateLabel ?? 'Beginning date', value: beginningDate });
    lines.push({ label: suspensionDateLabel ?? 'Suspension date', value: suspensionDate });
  } else {
    lines.push({ label: 'Period', value: `from ${beginningDate} to ${suspensionDate}` });
  }
  if (preAuthorizationNumberLabel) {
    lines.push({ label: preAuthorizationNumberLabel, value: balance.preAuthorizationNumber || '—' });
  }
  return lines;
};

export const mapToBalanceFormRow = (balance: PartnerMemberAllowanceBalance): BalanceFormRow => ({
  key: `existing-${balance.id}`,
  id: balance.id,
  amount: Number(balance.amount),
  overrideRenewAmount:
    balance.overrideRenewAmount === null || balance.overrideRenewAmount === undefined ? null : Number(balance.overrideRenewAmount),
  startBudgetAmount: Number(balance.startBudgetAmount ?? 0),
  beginningDate: balance.beginningDate ?? '',
  suspensionDate: balance.suspensionDate ?? '',
  preAuthorizationNumber: balance.preAuthorizationNumber ?? '',
});

export const buildAllowanceFormState = ({
  allowances,
  balances,
  budgetOnly = false,
}: {
  allowances: PartnerSubsidyAllowance[];
  balances: PartnerMemberAllowanceBalances['balances'];
  budgetOnly?: boolean;
}) => {
  const balancesByAllowanceId = new Map<number, PartnerMemberAllowanceBalances['balances']>();
  for (const balance of balances) {
    const allowanceBalances = balancesByAllowanceId.get(balance.subsidyAllowanceId) ?? [];
    allowanceBalances.push(balance);
    balancesByAllowanceId.set(balance.subsidyAllowanceId, allowanceBalances);
  }

  const originals = new Map<number, BalanceFormRow>();
  const groups = allowances.flatMap((allowance): AllowanceFormGroup[] => {
    const isBudget = isBudgetAllowanceKind(allowance.kind);
    if (budgetOnly && (!isBudget || allowance.isPartnerLevel)) {
      return [];
    }
    const allowanceBalances = (balancesByAllowanceId.get(allowance.id) ?? [])
      .map((balance) => {
        const row = mapToBalanceFormRow(balance);
        originals.set(balance.id, { ...row });
        return row;
      })
      .sort((left, right) => left.beginningDate.localeCompare(right.beginningDate));
    if (!isBudget && !allowanceBalances.length) {
      return [];
    }
    return [
      {
        allowanceId: allowance.id,
        allowanceName: allowance.allowanceName,
        kind: allowance.kind,
        isBudget,
        budgetLimit: allowance.budgetLimit ?? null,
        isPreAuthorizationNumberRequired: allowance.isPreAuthorizationNumberRequired ?? false,
        balances: allowanceBalances,
      },
    ];
  });

  return { groups, originals };
};

export const isCurrentBudget = (balance: BalanceFormRow, currentDate: string) =>
  Boolean(balance.beginningDate && balance.suspensionDate && balance.beginningDate <= currentDate && balance.suspensionDate >= currentDate);

export const isPastBudget = (balance: BalanceFormRow, currentDate: string) =>
  Boolean(balance.suspensionDate && balance.suspensionDate < currentDate);

export const getLatestPastBudgetSuspensionDate = (balances: BalanceFormRow[], currentDate: string) => {
  const pastDates = balances
    .filter((balance) => isPastBudget(balance, currentDate))
    .map((balance) => balance.suspensionDate)
    .filter(Boolean);
  if (!pastDates.length) {
    return '';
  }
  return pastDates.reduce((latest, date) => (date > latest ? date : latest));
};

export const countsTowardBudgetLimit = (balance: BalanceFormRow, currentDate: string) => !isPastBudget(balance, currentDate);

export type BalancePeriod = 'current' | 'future' | 'past';

export const getBalancePeriod = (balance: BalanceFormRow, currentDate: string): BalancePeriod => {
  if (isCurrentBudget(balance, currentDate)) {
    return 'current';
  }
  return isPastBudget(balance, currentDate) ? 'past' : 'future';
};

export const getBudgetOrderNumbers = (group: AllowanceFormGroup) => {
  const ordered = group.balances
    .map((balance, index) => ({ balance, index }))
    .sort((left, right) => {
      // Budgets without a beginning date yet (newly added rows) go last, in insertion order.
      if (!left.balance.beginningDate || !right.balance.beginningDate) {
        return Number(Boolean(right.balance.beginningDate)) - Number(Boolean(left.balance.beginningDate)) || left.index - right.index;
      }
      return left.balance.beginningDate.localeCompare(right.balance.beginningDate) || left.index - right.index;
    });
  return new Map(ordered.map(({ balance }, position) => [balance.key, position + 1]));
};

export const areBudgetFieldsEqual = (left: BalanceFormRow, right: BalanceFormRow) =>
  left.amount === right.amount &&
  left.startBudgetAmount === right.startBudgetAmount &&
  left.beginningDate === right.beginningDate &&
  left.suspensionDate === right.suspensionDate &&
  left.preAuthorizationNumber.trim() === right.preAuthorizationNumber.trim();

export const areNonBudgetFieldsEqual = (left: BalanceFormRow, right: BalanceFormRow) =>
  left.amount === right.amount && left.overrideRenewAmount === right.overrideRenewAmount;

export const buildBalanceChanges = (groups: AllowanceFormGroup[], originals: Map<number, BalanceFormRow>) => {
  const updates: SubsidyAllowanceBalanceUpdateInput[] = [];
  const creates: SubsidyAllowanceBalanceCreateInput[] = [];

  for (const group of groups) {
    for (const balance of group.balances) {
      if (balance.id === null) {
        creates.push({
          subsidyAllowanceId: group.allowanceId,
          amount: balance.amount,
          startBudgetAmount: balance.startBudgetAmount,
          beginningDate: balance.beginningDate,
          suspensionDate: balance.suspensionDate,
          preAuthorizationNumber: balance.preAuthorizationNumber.trim() || null,
        });
        continue;
      }

      const original = originals.get(balance.id);
      if (!original || (group.isBudget ? areBudgetFieldsEqual(balance, original) : areNonBudgetFieldsEqual(balance, original))) {
        continue;
      }

      updates.push(
        group.isBudget
          ? {
              id: balance.id,
              amount: balance.amount,
              startBudgetAmount: balance.startBudgetAmount,
              beginningDate: balance.beginningDate,
              suspensionDate: balance.suspensionDate,
              preAuthorizationNumber: balance.preAuthorizationNumber.trim() || null,
            }
          : {
              id: balance.id,
              amount: balance.amount,
              overrideRenewAmount: balance.overrideRenewAmount,
            },
      );
    }
  }

  return { updates, creates };
};

// Past budgets are read-only in the right panel, so their dates act as a floor there. The
// update partner subsidy page edits every period, so it opts out and relies on the overlap check.
export type BalanceValidationOptions = { enforcePastBudgetBoundary: boolean };

const DEFAULT_BALANCE_VALIDATION_OPTIONS: BalanceValidationOptions = { enforcePastBudgetBoundary: true };

const getBalanceAmountsError = (group: AllowanceFormGroup, balance: BalanceFormRow) => {
  if (!Number.isFinite(balance.amount) || balance.amount < 0) {
    return 'Allowance amounts must be non-negative numbers.';
  }
  if (RIDES_ALLOWANCE_KINDS.has(group.kind) && !Number.isInteger(balance.amount)) {
    return 'Ride allowance amounts must be whole numbers.';
  }
  if (!group.isBudget) {
    if (balance.overrideRenewAmount !== null && balance.overrideRenewAmount < 0) {
      return 'Override renew amounts must be non-negative.';
    }
    if (RIDES_ALLOWANCE_KINDS.has(group.kind) && balance.overrideRenewAmount !== null && !Number.isInteger(balance.overrideRenewAmount)) {
      return 'Override renew amounts must be whole numbers.';
    }
    return '';
  }
  if (!Number.isFinite(balance.startBudgetAmount) || balance.startBudgetAmount < 0) {
    return 'Start budget amounts must be non-negative numbers.';
  }
  if (RIDES_ALLOWANCE_KINDS.has(group.kind) && !Number.isInteger(balance.startBudgetAmount)) {
    return 'Start budget amounts must be whole numbers.';
  }
  if (!balance.beginningDate || !balance.suspensionDate || balance.beginningDate > balance.suspensionDate) {
    return 'Each budget needs a valid beginning and suspension date.';
  }
  if (group.isPreAuthorizationNumberRequired && !balance.preAuthorizationNumber.trim()) {
    return `${group.allowanceName} pre-authorization number is required.`;
  }
  return '';
};

const getBudgetPeriodError = (
  group: AllowanceFormGroup,
  balance: BalanceFormRow,
  currentDate: string,
  { enforcePastBudgetBoundary }: BalanceValidationOptions,
) => {
  if (enforcePastBudgetBoundary) {
    if (isPastBudget(balance, currentDate)) {
      return '';
    }
    const previousSuspensionDate = getLatestPastBudgetSuspensionDate(group.balances, currentDate);
    if (previousSuspensionDate && (balance.beginningDate <= previousSuspensionDate || balance.suspensionDate <= previousSuspensionDate)) {
      return `${group.allowanceName} dates must be after the previous budget's suspension date (${previousSuspensionDate}).`;
    }
  }
  const overlapsAnotherPeriod = group.balances.some(
    (other) =>
      other.key !== balance.key &&
      other.beginningDate &&
      other.suspensionDate &&
      other.beginningDate <= balance.suspensionDate &&
      balance.beginningDate <= other.suspensionDate,
  );
  if (overlapsAnotherPeriod) {
    return `${group.allowanceName} budget periods cannot overlap.`;
  }
  if (
    group.budgetLimit !== null &&
    group.balances.filter((item) => countsTowardBudgetLimit(item, currentDate)).length > group.budgetLimit
  ) {
    return `${group.allowanceName} can have at most ${group.budgetLimit} current or future budget periods.`;
  }
  return '';
};

export const getBalanceRowError = (
  group: AllowanceFormGroup,
  balance: BalanceFormRow,
  currentDate: string,
  options: BalanceValidationOptions = DEFAULT_BALANCE_VALIDATION_OPTIONS,
) => {
  return getBalanceAmountsError(group, balance) || (group.isBudget ? getBudgetPeriodError(group, balance, currentDate, options) : '');
};
